import React from "react";
import { useEffect, useState } from "react";
import { useSettingContext } from "context/SettingContext";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Checkbox } from "@mui/material";
import styled from "styled-components";
import { Check } from "@mui/icons-material";
import RemoveModal from "components/ui/RemoveModal/RemoveModal";
import { getSpecificLaborChart, updateLabourChart, useLabourChartData, useSelectUser } from "./apis/LabourChartsApi";
import { usePermissions } from "context/PermissionsContext";
import { findSpecificMenu, findSpecificSubMenu, isAllowed } from "utils/checkUserRigths";

const StyledTableContainer = styled(TableContainer)`
  background-color: transparent;
  border: 1px solid #fff;
  max-height: 75vh;
  overflow: auto;
  position: relative;

  &::-webkit-scrollbar {
    width: 0;
    height: 0;
  }
  scrollbar-width: none;
  -ms-overflow-style: none;
`;

const StyledTable = styled(Table)`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  & td,
  & th {
    font-size: 13px;
    font-family: "Poppins", sans-serif;
    font-weight: 250;
    line-height: 22px;
    color: #ffffff;
    text-align: center;
    border: 1px solid #fff;
  }
  & th {
    background-color: #444444;
    font-weight: semibold;
  }
`;

const StyledTableHeaderCell = styled(TableCell)`
  width: 33.33%;
  padding: 15px;
  font-size: 13px !important;
  font-weight: 400 !important;
  background-color: #444444;
  color: white;
  text-align: center !important;
  position: sticky;
  top: 0;
  z-index: 10;
  border: none !important;
`;

const StyledTableRow = styled(TableRow)`
  border-bottom: 1px solid #fff;
  & td {
    padding: 5px;
  }
`;

const StyledTableBodyCell = styled(TableCell)`
  width: 33.33%;
  padding: 10px !important;
  text-align: center !important;
  font-family: "Karla" !important;
  font-weight: 400 !important;
  font-size: 16px !important;
  line-height: 22px !important;
  letter-spacing: -0.41px !important;
`;

const CustomUncheckedIcon = styled("span")`
  width: 24px;
  height: 24px;
  border-radius: 5px;
  border: 2px solid white;
  background-color: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CustomCheckedIcon = styled("span")`
  width: 24px;
  height: 24px;
  border-radius: 5px;
  background-color: #50c878;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;

  & svg {
    width: 20px;
    height: 20px;
  }
`;

const Labour_Rights = () => {
  const [labourData, setLabourData] = useState([]);
  const [backendLabourData, setBackendLaborData] = useState([]);

  const { data } = useSelectUser();
  const { data: labourRightsData, isLoading } = useLabourChartData();

  const users = data?.data?.User;

  const { setSelectRoleBtnName, setRateMaster, saveRightModalOpen, setSaveRightModalOpen, setSelectRoleOptions, selectRoleOptions, setEnableSaveLimitsBtn, selectRoleBtnName, setVibrateButton } =
    useSettingContext();

  // const labourRightsData = [
  //   { srNo: 1, labourChart: "B2B", labourChart_ID: 1, currency: "₹", access: false },
  //   { srNo: 2, labourChart: "D2C", labourChart_ID: 10, currency: "$", access: false },
  //   { srNo: 3, labourChart: "B2B-USD", labourChart_ID: 11, currency: "₹", access: false },
  //   { srNo: 4, labourChart: "Internal", labourChart_ID: 12, currency: "$", access: false },
  //   { srNo: 5, labourChart: "Mounting", labourChart_ID: 16, currency: "₹", access: false },
  // ];

  useEffect(() => {
    setRateMaster(true);
    setSelectRoleBtnName("Select User");
    if (Array.isArray(users)) {
      const options = users?.map((data, index) => ({
        roleName: data?.username,
        userId: data?._id,
      }));
      setSelectRoleOptions(options);
    }
    setLabourData(labourRightsData?.map((item) => ({ ...item, access: false })));

    return () => {
      setRateMaster(false);
      setSelectRoleOptions([]);
      setSelectRoleBtnName("");
      setEnableSaveLimitsBtn(false);
      setVibrateButton(false);
      setLabourData([]);
    };
  }, [users, labourRightsData]);

  const compareChanges = () => {
    let getNewLabour = labourData?.filter((labor) => labor.access === true).map((labor) => labor.LabourChartCode);

    let noChange = backendLabourData?.length === getNewLabour?.length && [...backendLabourData].sort().every((val, i) => val === [...getNewLabour].sort()[i]);
    return !noChange;
  };

  const permission=usePermissions();
const hasEditAccess = isAllowed(findSpecificSubMenu(findSpecificMenu("Vision Web", permission), "Labour Rights"), "edit");

  const handleCheckboxChange = (index) => {
    if (!hasEditAccess) return;
    if (labourData[index]?.LabourChartCode !== "D2C-12W") {
      const updatedData = [...labourData];
      updatedData[index].access = !updatedData[index].access;

      setLabourData(updatedData);
      setEnableSaveLimitsBtn(compareChanges());
    }
  };

  const discardChanges = () => {
    setSaveRightModalOpen(false);
  };

  const saveChanges = async () => {
    if (!selectRoleBtnName || selectRoleBtnName === "Select User") return;

    const labourCharts = labourData.filter((item) => item.access)?.map((item) => ({ name: item.LabourChartCode, ID: item.LabourChartId, currencySymbol: item.CurrencySymbol }));

    const selectedUser = selectRoleOptions.find(({ roleName }) => roleName === selectRoleBtnName);

    if (!selectedUser?.userId) {
      alert("User not found for selected role");
      return;
    }

    const payload = {
      userId: selectedUser.userId,
      labourCharts,
    };

    try {
      const { status } = await updateLabourChart(payload);

      if (status === 200) {
        setSaveRightModalOpen(false);
        setEnableSaveLimitsBtn(false);
      }
    } catch (error) {
      console.error("Error updating rate master:", error);
    }
  };

  const btnInfo = [
    {
      btnName: "No",
      bgColor: "#E77B7B",
      click: () => {
        discardChanges();
      },
    },
    {
      btnName: "Yes",
      bgColor: "#50c878",
      click: () => {
        saveChanges();
      },
    },
  ];

  useEffect(() => {
    const fetchLabourChartOffUser = async () => {
      try {
        const selectedUser = selectRoleOptions.find(({ roleName }) => roleName === selectRoleBtnName);

        if (!selectedUser?.userId) return;

        const labourCharts = await getSpecificLaborChart(selectedUser?.userId);

        if (labourCharts?.status === 200 && Array.isArray(labourCharts.labourCharts)) {
          const updatedTableData = labourRightsData.map((item) => ({
            ...item,
            access: labourCharts?.labourCharts.includes(item?.LabourChartCode),
          }));

          setLabourData(updatedTableData);
          setBackendLaborData(labourCharts?.labourCharts);
        } else {
          const clearedTableData = labourRightsData.map((item) => ({
            ...item,
            access: false,
          }));
          setLabourData(clearedTableData);
        }
      } catch (error) {
        console.error("Error fetching rate master for user", error);
        const clearedTableData = labourRightsData.map((item) => ({
          ...item,
          access: false,
        }));
        setLabourData(clearedTableData);
      }
    };

    if (selectRoleBtnName && selectRoleBtnName !== "Select User") {
      fetchLabourChartOffUser();
    }
  }, [selectRoleBtnName]);

  const formatCurrency = (currency) => {
    let filterCurrency = currency.replace(/[A-Za-z]/g, "").trim();
    return filterCurrency ? filterCurrency : currency.trim();
  };

  return (
    <>
      <div style={{ marginTop: "15px", marginLeft: "6px", marginRight: "11.5px", backgroundColor: "#000", padding: "10px" }}></div>
      <StyledTableContainer>
        <StyledTable>
          <TableHead>
            <TableRow>
              <StyledTableHeaderCell sx={{ width: "20%", border: "1px solid #fff" }}>Sr no.</StyledTableHeaderCell>
              <StyledTableHeaderCell sx={{ width: "20%" }}>Labour Chart</StyledTableHeaderCell>
              <StyledTableHeaderCell sx={{ width: "20%" }}>Labour Chart ID</StyledTableHeaderCell>
              <StyledTableHeaderCell sx={{ width: "20%" }}>Currency</StyledTableHeaderCell>
              <StyledTableHeaderCell sx={{ width: "20%" }}>Access</StyledTableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody sx={{ fontWeight: 100 }}>
            {labourData?.map((item, index) => (
              <StyledTableRow key={index}>
                <StyledTableBodyCell sx={{ width: "20%" }}>{index + 1}.</StyledTableBodyCell>
                <StyledTableBodyCell sx={{ width: "20%" }}>{item.LabourChartCode}</StyledTableBodyCell>
                <StyledTableBodyCell sx={{ width: "20%" }}>{item.LabourChartId}</StyledTableBodyCell>
                <StyledTableBodyCell sx={{ width: "20%" }}>{formatCurrency(item?.CurrencySymbol)}</StyledTableBodyCell>
                <StyledTableBodyCell sx={{ width: "20%" }}>

                {handleCheckboxChange && (
                  <Checkbox
                    color="success"
                    disableRipple
                    checked={item.access}
                    disabled={!hasEditAccess}
                    onChange={selectRoleBtnName === "Select User" ? () => setVibrateButton(true) : () => handleCheckboxChange(index)}
                    icon={<CustomUncheckedIcon />}
                    checkedIcon={
                      <CustomCheckedIcon>
                        <Check />
                      </CustomCheckedIcon>
                    }
                    sx={{ cursor: hasEditAccess && item.LabourChartCode === "D2C-12W" ? "default" : "pointer" }}
                  ></Checkbox>
                )}
                </StyledTableBodyCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </StyledTable>
      </StyledTableContainer>
      <RemoveModal open={saveRightModalOpen} setOpen={setSaveRightModalOpen} btnInfo={btnInfo} heading="Do you want to save access changes?" />
    </>
  );
};

export default Labour_Rights;
